import { useTranslation } from 'react-i18next';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { formatDate, formatRelativeTimeI18n } from '@/utils/date';
import { CameraStatusBadge } from '@/components/CameraStatusBadge';
import { useCameraValidation } from '@/features/CameraTable/hooks';

interface CameraStatusTooltipProps {
    sourceId?: string;
    live?: boolean;
    errorType?: string;
    lastCheckedAt?: Date;
    loading?: boolean;
    stale?: boolean;
    side?: 'left' | 'right' | 'top' | 'bottom';
}

export function CameraStatusTooltip({
    sourceId,
    live: liveProp,
    errorType: errorTypeProp,
    lastCheckedAt: lastCheckedAtProp,
    loading: loadingProp,
    stale,
    side = 'right',
}: CameraStatusTooltipProps) {
    const { t } = useTranslation(I18nNamespace.CAMERAS);

    const hookResult = useCameraValidation(sourceId || '');
    const useHook = !!sourceId;

    const live = useHook ? hookResult.live : liveProp;
    const errorType = useHook ? hookResult.errorType : errorTypeProp;
    const lastCheckedAt = useHook
        ? hookResult.lastCheckedAt
        : lastCheckedAtProp;
    const loading = useHook ? hookResult.isLoading : loadingProp;

    if (stale) {
        return (
            <div>
                <CameraStatusBadge
                    live={live}
                    loading={loading}
                    stale={stale}
                />
            </div>
        );
    }

    const title =
        live === undefined
            ? t('status.checking')
            : live
              ? t('status.success')
              : `${t('status.failed')}${errorType ? `: ${errorType}` : ''}.`;

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <div>
                    <CameraStatusBadge
                        live={live}
                        loading={loading}
                        stale={stale}
                    />
                </div>
            </TooltipTrigger>
            <TooltipContent
                side={side}
                sideOffset={side === 'right' ? -15 : 5}
                variant="panel"
                className="min-w-[280px]"
            >
                <div className="flex gap-3 items-center">
                    <div className="flex-shrink-0">
                        <CameraStatusBadge live={live} />
                    </div>
                    <div className="flex flex-col gap-1">
                        <span className="text-white-100 text-sm">{title}</span>
                        {lastCheckedAt && (
                            <span
                                className="text-grey-300 text-xs"
                                title={formatDate(lastCheckedAt)}
                            >
                                {t('status.lastCheck')}{' '}
                                {formatRelativeTimeI18n(lastCheckedAt)}
                            </span>
                        )}
                    </div>
                </div>
            </TooltipContent>
        </Tooltip>
    );
}
